import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, FileText, Upload, Link as LinkIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { getModule, getModuleSubmission, getUserProgress, createSubmission, resubmitSubmission } from '../lib/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../lib/firebase';

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const ALLOWED_UPLOAD_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'image/jpeg',
  'image/png'
];

const ModulePage = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [module, setModule] = useState(null);
  const [existingSubmission, setExistingSubmission] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchModuleData = useCallback(async () => {
    try {
      const moduleData = await getModule(moduleId);
      setModule(moduleData);
      
      if (user) {
        if (user.role === 'student') {
          const progress = await getUserProgress(user.id);
          const canOpenModule =
            progress.unlockedModuleIds?.includes(moduleId) ||
            progress.completedModuleIds?.includes(moduleId);

          if (!canOpenModule) {
            toast.error('Complete the previous module before opening this one.');
            navigate('/dashboard');
            return;
          }
        }

        const submission = await getModuleSubmission(user.id, moduleId);
        setExistingSubmission(submission);
      }
    } catch (error) {
      toast.error('Failed to load module');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [moduleId, navigate, user]);

  useEffect(() => {
    fetchModuleData();
  }, [fetchModuleData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
        <div className="text-[#00E5FF] text-xl">Loading...</div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
        <div className="text-[#EF4444] text-xl">Module not found</div>
      </div>
    );
  }

  return (
    <div data-testid="module-page" className="min-h-screen bg-[#0B0E14] pb-12">
      <div className="bg-[#151923] border-b border-[#1E293B]">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Button
            data-testid="back-to-dashboard-btn"
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4 text-[#00E5FF] hover:text-[#33EBFF] hover:bg-[#1E293B]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs font-mono text-[#64748B] bg-[#1E293B] px-2 py-1 rounded">
              Module {module.order}
            </span>
          </div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {module.title}
          </h1>
          <p className="text-[#94A3B8] mt-2">{module.description}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 mt-8">
        {/* Module Content */}
        {module.content && (
          <div className="bg-[#151923] border border-[#1E293B] rounded-md p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Module Content
            </h2>
            <div className="prose prose-invert max-w-none text-[#94A3B8]">
              {module.content}
            </div>
          </div>
        )}

        {/* Assignment Section */}
        <AssignmentCard 
          module={module} 
          existingSubmission={existingSubmission}
          onSubmissionCreated={() => fetchModuleData()}
        />
      </div>
    </div>
  );
};

const AssignmentCard = ({ module, existingSubmission, onSubmissionCreated }) => {
  const { user } = useAuth();
  const [showSubmit, setShowSubmit] = useState(false);
  const [formData, setFormData] = useState({
    textAnswer: '',
    links: ''
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let fileUrls = [];

      const invalidFile = files.find((file) => (
        file.size > MAX_UPLOAD_BYTES || !ALLOWED_UPLOAD_TYPES.includes(file.type)
      ));

      if (invalidFile) {
        toast.error('Upload PDF, DOCX, TXT, JPG, or PNG files up to 10 MB.');
        return;
      }
      
      // Upload files to Firebase Storage if any
      if (files.length > 0) {
        for (const file of files) {
          const storageRef = ref(storage, `submissions/${user.id}/${module.id}/${Date.now()}_${file.name}`);
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          fileUrls.push({ name: file.name, url });
        }
      }

      const submissionPayload = {
        studentId: user.id,
        studentName: user.name,
        studentEmail: user.email,
        moduleId: module.id,
        moduleTitle: module.title,
        assignmentTitle: `${module.title} Assignment`,
        textAnswer: formData.textAnswer,
        links: formData.links.split(',').map(l => l.trim()).filter(l => l),
        fileUrls
      };

      if (existingSubmission?.status === 'rejected') {
        await resubmitSubmission(existingSubmission.id, {
          textAnswer: submissionPayload.textAnswer,
          links: submissionPayload.links,
          fileUrls: submissionPayload.fileUrls
        });
      } else {
        await createSubmission(submissionPayload);
      }

      toast.success('Assignment submitted successfully!');
      setShowSubmit(false);
      setFormData({ textAnswer: '', links: '' });
      setFiles([]);
      onSubmissionCreated();
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit assignment');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-[#6366F1]/20', text: 'text-[#6366F1]', label: 'PENDING REVIEW' },
      in_review: { bg: 'bg-[#F59E0B]/20', text: 'text-[#F59E0B]', label: 'IN REVIEW' },
      approved: { bg: 'bg-[#10B981]/20', text: 'text-[#10B981]', label: 'APPROVED' },
      rejected: { bg: 'bg-[#EF4444]/20', text: 'text-[#EF4444]', label: 'NEEDS REVISION' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-3 py-1 rounded text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div data-testid={`assignment-${module.id}`} className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 rounded-md bg-[#6366F1]/20 flex items-center justify-center">
          <FileText className="w-6 h-6 text-[#6366F1]" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Module Assignment
            </h3>
            {existingSubmission && getStatusBadge(existingSubmission.status)}
          </div>
          
          <p className="text-[#94A3B8] mb-4">
            Complete this assignment to unlock the next module. Submit your work including any code, documentation, or links to demonstrate your understanding.
          </p>
          
          {/* Show existing submission */}
          {existingSubmission && (
            <div className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-4 mb-4">
              <div className="text-sm font-semibold text-[#00E5FF] mb-3">Your Submission</div>
              
              {existingSubmission.textAnswer && (
                <div className="mb-3">
                  <div className="text-xs text-[#64748B] mb-1">Answer:</div>
                  <div className="text-sm text-[#94A3B8]">{existingSubmission.textAnswer}</div>
                </div>
              )}
              
              {existingSubmission.links?.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs text-[#64748B] mb-1">Links:</div>
                  <div className="flex flex-wrap gap-2">
                    {existingSubmission.links.map((link, idx) => (
                      <a 
                        key={idx} 
                        href={link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-[#00E5FF] hover:underline flex items-center gap-1"
                      >
                        <LinkIcon className="w-3 h-3" />
                        {link}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              {existingSubmission.fileUrls?.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs text-[#64748B] mb-1">Files:</div>
                  <div className="flex flex-wrap gap-2">
                    {existingSubmission.fileUrls.map((file, idx) => (
                      <a 
                        key={idx} 
                        href={file.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-[#00E5FF] hover:underline"
                      >
                        {file.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              {existingSubmission.feedback && (
                <div className="mt-4 pt-4 border-t border-[#1E293B]">
                  <div className="text-xs text-[#64748B] mb-1">Instructor Feedback:</div>
                  <div className="text-sm text-[#F8FAFC]">{existingSubmission.feedback}</div>
                </div>
              )}
            </div>
          )}
          
          {/* Show submit form or button */}
          {!existingSubmission || existingSubmission.status === 'rejected' ? (
            !showSubmit ? (
              <Button
                data-testid={`submit-assignment-btn-${module.id}`}
                onClick={() => setShowSubmit(true)}
                className="bg-[#00E5FF] text-black hover:bg-[#33EBFF] rounded-md"
              >
                {existingSubmission?.status === 'rejected' ? 'Resubmit Assignment' : 'Submit Assignment'}
              </Button>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                <div>
                  <Label htmlFor="textAnswer" className="text-[#F8FAFC]">Your Answer</Label>
                  <Textarea
                    data-testid="assignment-answer-textarea"
                    id="textAnswer"
                    value={formData.textAnswer}
                    onChange={(e) => setFormData({ ...formData, textAnswer: e.target.value })}
                    className="bg-[#0B0E14] border-[#1E293B] text-[#F8FAFC] mt-2 min-h-[150px]"
                    placeholder="Describe your solution and approach..."
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="links" className="text-[#F8FAFC]">Links (comma-separated)</Label>
                  <Input
                    data-testid="assignment-links-input"
                    id="links"
                    type="text"
                    value={formData.links}
                    onChange={(e) => setFormData({ ...formData, links: e.target.value })}
                    className="bg-[#0B0E14] border-[#1E293B] text-[#F8FAFC] mt-2"
                    placeholder="GitHub repo, live demo, etc."
                  />
                </div>
                
                <div>
                  <Label htmlFor="files" className="text-[#F8FAFC] flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    Upload Files (optional)
                  </Label>
                  <Input
                    data-testid="assignment-files-input"
                    id="files"
                    type="file"
                    multiple
                    accept=".pdf,.docx,.txt,.jpg,.jpeg,.png"
                    onChange={(e) => setFiles(Array.from(e.target.files))}
                    className="bg-[#0B0E14] border-[#1E293B] text-[#F8FAFC] mt-2"
                  />
                  {files.length > 0 && (
                    <div className="text-sm text-[#94A3B8] mt-2">
                      {files.length} file(s) selected
                    </div>
                  )}
                </div>
                
                <div className="flex gap-3">
                  <Button
                    data-testid="submit-btn"
                    type="submit"
                    className="bg-[#00E5FF] text-black hover:bg-[#33EBFF] rounded-md"
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit'}
                  </Button>
                  <Button
                    data-testid="cancel-btn"
                    type="button"
                    variant="outline"
                    onClick={() => setShowSubmit(false)}
                    className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )
          ) : (
            <div className="text-sm text-[#94A3B8]">
              {existingSubmission.status === 'approved' 
                ? 'Great job! Your submission has been approved.'
                : 'Your submission is being reviewed.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModulePage;
